import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    BsChevronLeft, BsCameraFill, BsSaveFill,
    BsInfoCircleFill, BsPersonVcardFill, BsTrophyFill,
    BsSpeedometer2, BsRulers, BsGenderAmbiguous
} from 'react-icons/bs';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import { AppContext } from '../../context/AppContext';

const ManagerAddAthlete = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { uuid } = useParams();
    const { masterData } = useContext(AppContext);

    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const isEditMode = !!uuid;

    const KYORUGI_FESTIVAL_BELTS = [
        'Putih',
        'Kuning',
        'Kuning Strip',
        'Hijau',
        'Hijau Strip'
    ];

    const POOMSAE_CATEGORY_OPTIONS = ['Individual', 'Freestyle', 'Pair', 'Team'];

    const normalizePoomsaeCategoryValue = (value) => {
        const raw = String(value || '').toLowerCase().trim();

        if (raw.includes('freestyle') || raw.includes('free style')) return 'Freestyle';
        if (raw.includes('pair') || raw.includes('pasangan') || raw.includes('ganda')) return 'Pair';
        if (raw.includes('team') || raw.includes('regu') || raw.includes('kelompok')) return 'Team';

        return 'Individual';
    };

    // Ambil default values dari Smart Button "Tambah" di halaman sebelumnya
    const defaultType = location.state?.preselectedType || 'Festival';
    const defaultCompType = location.state?.preselectedCompType || 'Kyorugi';

    const [formData, setFormData] = useState({
        name: '',
        gender: 'Lelaki',
        birth_year: '',
        division: '',
        type: defaultType,
        competition_type: defaultCompType,
        weight_class: '',
        declared_weight: '',
        height: '',
        poomsae_category: defaultCompType === 'Poomsae' ? 'Individual' : '',
        belt: ''
    });

    const [entryMembers, setEntryMembers] = useState({
        member1: '',
        member2: '',
        member3: ''
    });

    const [imageFile, setImageFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const BACKEND_URL = process.env.REACT_APP_BACKEND || '';

    // ==============================================================
    // HELPER PENGAMBIL DATA FAIL-SAFE
    // ==============================================================
    const getConfig = () => {
        if (!masterData) return {};

        let data = masterData;

        if (data.result) data = data.result;
        else if (data.data) data = data.data;

        if (Array.isArray(data)) data = data[0];

        if (data?.config) {
            if (typeof data.config === 'string') {
                try { return JSON.parse(data.config); } catch (e) { return {}; }
            }
            return data.config;
        }

        return data || {};
    };

    const getBeltOptions = () => {
        const config = getConfig();
        return config.belt_levels || config.beltLevels || [];
    };

    const getPoomsaeBeltOptions = () => {
        const belts = getBeltOptions();

        return belts.length > 0 ? belts : [
            'Putih',
            'Kuning',
            'Kuning Strip',
            'Hijau',
            'Hijau Strip',
            'Biru',
            'Biru Strip',
            'Merah',
            'Merah Strip',
            'Poom',
            'Hitam'
        ];
    };

    const parseMembers = (members, fallbackName = '') => {
        if (Array.isArray(members)) {
            return members
                .map(item => String(item || '').trim())
                .filter(Boolean);
        }

        if (typeof members === 'string' && members.trim() !== '') {
            try {
                const parsed = JSON.parse(members);

                if (Array.isArray(parsed)) {
                    return parsed
                        .map(item => String(item || '').trim())
                        .filter(Boolean);
                }
            } catch (error) {
                return members
                    .split(/\s*(?:\/|\||,|\+|&|\bdan\b|\n)\s*/i)
                    .map(item => String(item || '').trim())
                    .filter(Boolean);
            }
        }

        if (fallbackName) {
            return String(fallbackName)
                .split(/\s*(?:\/|\||,|\+|&|\bdan\b|\n)\s*/i)
                .map(item => String(item || '').trim())
                .filter(Boolean);
        }

        return [];
    };

    // 1. Hitung Divisi
    const calculateDivisionDynamic = (birthYear) => {
        const config = getConfig();

        if (!birthYear || birthYear.toString().length !== 4 || !config.divisions) return "";

        const eventYear = parseInt(config.event_year) || new Date().getFullYear();
        const age = eventYear - parseInt(birthYear);

        const foundDivision = config.divisions.find(div => {
            const min = parseInt(div.min_age) || 0;
            const max = parseInt(div.max_age) || 0;
            return age >= min && (max === 0 || age <= max);
        });

        return foundDivision ? foundDivision.name : "Tidak Layak (Usia)";
    };

    // 2. Ambil Opsi Kelas Tanding Berdasarkan Divisi
    const getFilteredWeightOptions = () => {
        const config = getConfig();
        const classesObj = config.weight_classes || config.weightClasses;

        if (!classesObj || !formData.division || !formData.gender) return [];

        const currentDiv = formData.division.toLowerCase().trim();
        const divKey = Object.keys(classesObj).find(k => k.toLowerCase().trim() === currentDiv);

        if (!divKey) return [];

        const divisionData = classesObj[divKey];
        const currentGender = formData.gender.toLowerCase().trim();

        let genderKey = Object.keys(divisionData).find(k => k.toLowerCase().trim() === currentGender);

        if (!genderKey) {
            if (['lelaki', 'laki-laki', 'l'].includes(currentGender)) {
                genderKey = Object.keys(divisionData).find(k => ['putra', 'male', 'pa', 'laki'].includes(k.toLowerCase().trim()));
            } else if (['perempuan', 'p'].includes(currentGender)) {
                genderKey = Object.keys(divisionData).find(k => ['putri', 'female', 'pi', 'cewek'].includes(k.toLowerCase().trim()));
            }
        }

        return genderKey ? (divisionData[genderKey] || []) : [];
    };

    // ==============================================================
    // EFFECTS
    // ==============================================================

    // --- 1. FETCH DATA JIKA MODE EDIT ---
    useEffect(() => {
        if (isEditMode) {
            const fetchAthleteDetail = async () => {
                setIsFetching(true);

                try {
                    const res = await api.get(`/athletes/${uuid}`);
                    const d = res.data;
                    const members = parseMembers(d.entry_members, d.name);

                    setFormData({
                        name: d.name || '',
                        gender: d.gender === 'Campuran' ? 'Lelaki' : (d.gender || 'Lelaki'),
                        birth_year: d.birth_year || '',
                        division: d.division || '',
                        type: d.type || 'Festival',
                        competition_type: d.competition_type || 'Kyorugi',
                        weight_class: d.weight_class || '',
                        declared_weight: d.declared_weight || '',
                        height: d.height || '',
                        poomsae_category: d.poomsae_category || (d.competition_type === 'Poomsae' ? 'Individual' : ''),
                        belt: d.belt || ''
                    });

                    setEntryMembers({
                        member1: members[0] || d.name || '',
                        member2: members[1] || '',
                        member3: members[2] || ''
                    });

                    if (d.image) {
                        setPreviewUrl(`${BACKEND_URL}/uploads/athletes/${d.image}`);
                    }
                } catch (error) {
                    toast.error("Gagal memuat data atlet.");
                    navigate('/manager/atlet');
                } finally {
                    setIsFetching(false);
                }
            };

            fetchAthleteDetail();
        }
    }, [uuid, isEditMode, navigate, BACKEND_URL]);

    // --- 2. LOGIKA OTOMATIS (DIVISI & KELAS) ---
    useEffect(() => {
        const birthYearStr = formData.birth_year?.toString() || '';
        const config = getConfig();

        // Auto Kalkulasi Divisi
        if (birthYearStr.length === 4 && config.divisions) {
            setFormData(prev => ({ ...prev, division: calculateDivisionDynamic(birthYearStr) || '' }));
        } else if (birthYearStr.length < 4) {
            setFormData(prev => ({ ...prev, division: '' }));
        }

        // Reset Kolom Tidak Relevan
        if (formData.competition_type === 'Poomsae') {
            setFormData(prev => ({
                ...prev,
                weight_class: '',
                declared_weight: '',
                height: '',
                poomsae_category: prev.poomsae_category || 'Individual'
            }));
        } else if (formData.competition_type === 'Kyorugi') {
            setFormData(prev => ({
                ...prev,
                poomsae_category: '',
                belt: prev.type === 'Festival' ? prev.belt : ''
            }));
        }

        if (formData.type === 'Festival') {
            setFormData(prev => ({ ...prev, weight_class: '' }));
        } else if (formData.type === 'Prestasi') {
            setFormData(prev => ({
                ...prev,
                declared_weight: '',
                height: '',
                belt: prev.competition_type === 'Poomsae' ? prev.belt : ''
            }));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.birth_year, formData.competition_type, formData.type, masterData]);

    // ==============================================================
    // HANDLERS
    // ==============================================================
    const handleCompetitionTypeChange = (competitionType) => {
        setFormData(prev => ({
            ...prev,
            competition_type: competitionType,
            poomsae_category: competitionType === 'Poomsae' ? 'Individual' : '',
            weight_class: '',
            declared_weight: '',
            height: '',
            belt: ''
        }));

        setEntryMembers({
            member1: '',
            member2: '',
            member3: ''
        });
    };

    const handleParticipantTypeChange = (participantType) => {
        setFormData(prev => ({
            ...prev,
            type: participantType,
            weight_class: '',
            declared_weight: '',
            height: '',
            belt: '',
            poomsae_category: prev.competition_type === 'Poomsae'
                ? (participantType === 'Prestasi' ? (prev.poomsae_category || 'Individual') : '')
                : ''
        }));
    };

    const handlePoomsaeCategoryChange = (category) => {
        const normalizedCategory = normalizePoomsaeCategoryValue(category);

        setFormData(prev => {
            const nextData = {
                ...prev,
                poomsae_category: normalizedCategory
            };

            if (normalizedCategory === 'Pair') {
                nextData.gender = 'Campuran';
            } else if (prev.gender === 'Campuran') {
                nextData.gender = 'Lelaki';
            }

            return nextData;
        });
    };

    const handleEntryMemberChange = (key, value) => {
        setEntryMembers(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file) {
            if (file.size > 2 * 1024 * 1024) return toast.warning("Ukuran foto maksimal 2MB");
            setImageFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const getActivePoomsaeCategory = (formElement = null) => {
        if (formData.competition_type !== 'Poomsae') return '';

        const formCategory = formElement?.elements?.poomsae_category?.value;
        return normalizePoomsaeCategoryValue(formCategory || formData.poomsae_category);
    };

    const getFinalEntryMembers = (poomsaeCategory = getActivePoomsaeCategory()) => {
        const isPoomsae = formData.competition_type === 'Poomsae';
        const category = poomsaeCategory;

        if (isPoomsae && category === 'Pair') {
            return [
                entryMembers.member1,
                entryMembers.member2
            ].map(item => String(item || '').trim()).filter(Boolean);
        }

        if (isPoomsae && category === 'Team') {
            return [
                entryMembers.member1,
                entryMembers.member2,
                entryMembers.member3
            ].map(item => String(item || '').trim()).filter(Boolean);
        }

        return [
            formData.name
        ].map(item => String(item || '').trim()).filter(Boolean);
    };

    const validateFormBeforeSubmit = (poomsaeCategory = getActivePoomsaeCategory()) => {
        const isKyorugi = formData.competition_type === 'Kyorugi';
        const isPoomsae = formData.competition_type === 'Poomsae';
        const isPrestasi = formData.type === 'Prestasi';
        const isFestival = formData.type === 'Festival';

        if ((formData.division || '').includes("Tidak Layak")) {
            toast.error("Usia atlet tidak memenuhi syarat atau belum diisi dengan benar.");
            return false;
        }

        if (isKyorugi && isFestival) {
            if (!formData.declared_weight) {
                toast.error("Berat badan wajib diisi untuk Kyorugi Festival.");
                return false;
            }

            if (!formData.height) {
                toast.error("Tinggi badan wajib diisi untuk Kyorugi Festival.");
                return false;
            }

            if (!formData.belt) {
                toast.error("Sabuk wajib dipilih untuk Kyorugi Festival.");
                return false;
            }

            if (!KYORUGI_FESTIVAL_BELTS.includes(formData.belt)) {
                toast.error("Kyorugi Festival hanya boleh untuk sabuk Putih sampai Hijau Strip.");
                return false;
            }
        }

        if (isPoomsae && isPrestasi && poomsaeCategory === 'Pair') {
            const members = getFinalEntryMembers(poomsaeCategory);

            if (members.length !== 2) {
                toast.error("Poomsae Pair wajib mengisi 2 nama atlet.");
                return false;
            }
        }

        if (isPoomsae && isPrestasi && poomsaeCategory === 'Team') {
            const members = getFinalEntryMembers(poomsaeCategory);

            if (members.length !== 3) {
                toast.error("Poomsae Team wajib mengisi 3 nama atlet.");
                return false;
            }

            if (!['Lelaki', 'Perempuan'].includes(formData.gender)) {
                toast.error("Poomsae Team wajib memilih Team Putra atau Team Putri.");
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const finalPoomsaeCategory = getActivePoomsaeCategory(e.currentTarget);

        if (!validateFormBeforeSubmit(finalPoomsaeCategory)) return;

        setIsLoading(true);

        try {
            const finalMembers = getFinalEntryMembers(finalPoomsaeCategory);

            let finalName = formData.name;
            let finalGender = formData.gender;

            if (formData.competition_type === 'Poomsae' && finalPoomsaeCategory === 'Pair') {
                finalName = finalMembers.join(' / ');
                finalGender = 'Campuran';
            }

            if (formData.competition_type === 'Poomsae' && finalPoomsaeCategory === 'Team') {
                finalName = finalMembers.join(' / ');
            }

            const finalPayload = {
                ...formData,
                poomsae_category: formData.competition_type === 'Poomsae' ? finalPoomsaeCategory : '',
                name: finalName,
                gender: finalGender
            };

            const data = new FormData();

            Object.keys(finalPayload).forEach(key => {
                if (
                    finalPayload[key] !== null &&
                    finalPayload[key] !== undefined &&
                    finalPayload[key] !== ''
                ) {
                    data.append(key, finalPayload[key]);
                }
            });

            if (finalMembers.length > 0) {
                data.append('entry_members', JSON.stringify(finalMembers));
            }

            if (imageFile) {
                data.append('image', imageFile);
            }

            const configParams = { headers: { 'Content-Type': 'multipart/form-data' } };

            if (isEditMode) {
                await api.put(`/athletes/${uuid}`, data, configParams);
                toast.success("Data atlet berhasil diperbarui!");
            } else {
                await api.post('/athletes', data, configParams);
                toast.success("Atlet berhasil didaftarkan!");
            }

            navigate(-1);
        } catch (error) {
            toast.error(error.response?.data?.msg || "Terjadi kesalahan saat menyimpan data.");
            console.error("Upload Error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!masterData || isFetching) {
        return (
            <div className="text-center p-5">
                <div className="spinner-border text-primary"></div>
            </div>
        );
    }

    const isKyorugi = formData.competition_type === 'Kyorugi';
    const isPoomsae = formData.competition_type === 'Poomsae';
    const isPrestasi = formData.type === 'Prestasi';
    const isFestival = formData.type === 'Festival';

    const isPoomsaePair = isPoomsae && isPrestasi && formData.poomsae_category === 'Pair';
    const isPoomsaeTeam = isPoomsae && isPrestasi && formData.poomsae_category === 'Team';
    const isPoomsaeGroupEntry = isPoomsaePair || isPoomsaeTeam;

    return (
        <div className="container-fluid p-0 animate-fade-in">
            <div className="d-flex align-items-center gap-3 mb-4">
                <button onClick={() => navigate(-1)} className="btn btn-light rounded-circle shadow-sm" style={{ width: '45px', height: '45px' }}>
                    <BsChevronLeft />
                </button>
                <div>
                    <h4 className="fw-bold mb-0">{isEditMode ? 'Edit Data Atlet' : 'Pendaftaran Atlet Baru'}</h4>
                    <p className="text-muted small mb-0">Lengkapi informasi atlet dengan benar</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="row g-4">
                {/* Kolom Kiri: Form Input */}
                <div className="col-lg-8">
                    <div className="card border-0 shadow-sm rounded-4 p-4">
                        <div className="row g-3">

                            {/* SECTION: KLASIFIKASI PERTANDINGAN */}
                            <div className="col-12 mb-2">
                                <h6 className="fw-bold text-dark border-bottom pb-2">Klasifikasi Pertandingan</h6>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label small fw-bold text-muted">Tipe Peserta</label>
                                <div className="row g-2">
                                    {['Prestasi', 'Festival'].map(cat => (
                                        <div className="col-6" key={cat}>
                                            <button
                                                type="button"
                                                className={`btn w-100 py-2 fw-bold transition-all ${formData.type === cat ? 'btn-warning text-white shadow-sm' : 'btn-outline-warning'}`}
                                                onClick={() => handleParticipantTypeChange(cat)}
                                            >
                                                {cat.toUpperCase()}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label small fw-bold text-muted">Kategori Tanding</label>
                                <div className="row g-2">
                                    {['Kyorugi', 'Poomsae'].map(type => (
                                        <div className="col-6" key={type}>
                                            <button
                                                type="button"
                                                className={`btn w-100 py-2 fw-bold transition-all ${formData.competition_type === type ? 'btn-primary shadow-sm' : 'btn-outline-primary'}`}
                                                onClick={() => handleCompetitionTypeChange(type)}
                                            >
                                                {type.toUpperCase()}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* SECTION: IDENTITAS */}
                            <div className="col-12 mt-4 mb-2">
                                <h6 className="fw-bold text-dark border-bottom pb-2">Identitas Atlet</h6>
                            </div>

                            {!isPoomsaeGroupEntry && (
                                <div className="col-md-12">
                                    <label className="form-label small fw-bold text-muted">Nama Lengkap Atlet</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-0"><BsPersonVcardFill /></span>
                                        <input
                                            type="text"
                                            className="form-control bg-light border-0 py-2"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="Masukkan nama sesuai ID..."
                                        />
                                    </div>
                                </div>
                            )}

                            {isPoomsaePair && (
                                <>
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold text-muted">Nama Atlet Putra</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-0"><BsPersonVcardFill /></span>
                                            <input
                                                type="text"
                                                className="form-control bg-light border-0 py-2"
                                                required
                                                value={entryMembers.member1}
                                                onChange={(e) => handleEntryMemberChange('member1', e.target.value)}
                                                placeholder="Masukkan nama atlet putra"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold text-muted">Nama Atlet Putri</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-0"><BsPersonVcardFill /></span>
                                            <input
                                                type="text"
                                                className="form-control bg-light border-0 py-2"
                                                required
                                                value={entryMembers.member2}
                                                onChange={(e) => handleEntryMemberChange('member2', e.target.value)}
                                                placeholder="Masukkan nama atlet putri"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {isPoomsaeTeam && (
                                <>
                                    <div className="col-md-4">
                                        <label className="form-label small fw-bold text-muted">Nama Atlet 1</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-0"><BsPersonVcardFill /></span>
                                            <input
                                                type="text"
                                                className="form-control bg-light border-0 py-2"
                                                required
                                                value={entryMembers.member1}
                                                onChange={(e) => handleEntryMemberChange('member1', e.target.value)}
                                                placeholder="Nama atlet 1"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label small fw-bold text-muted">Nama Atlet 2</label>
                                        <input
                                            type="text"
                                            className="form-control bg-light border-0 py-2"
                                            required
                                            value={entryMembers.member2}
                                            onChange={(e) => handleEntryMemberChange('member2', e.target.value)}
                                            placeholder="Nama atlet 2"
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label small fw-bold text-muted">Nama Atlet 3</label>
                                        <input
                                            type="text"
                                            className="form-control bg-light border-0 py-2"
                                            required
                                            value={entryMembers.member3}
                                            onChange={(e) => handleEntryMemberChange('member3', e.target.value)}
                                            placeholder="Nama atlet 3"
                                        />
                                    </div>
                                </>
                            )}

                            <div className="col-md-6">
                                <label className="form-label small fw-bold text-muted">
                                    {isPoomsaePair ? 'Jenis Kelamin' : isPoomsaeTeam ? 'Jenis Team' : 'Jenis Kelamin'}
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-0"><BsGenderAmbiguous /></span>

                                    {isPoomsaePair ? (
                                        <input
                                            type="text"
                                            className="form-control bg-light border-0 py-2 fw-bold"
                                            value="Campuran / Pair Lelaki dan Wanita"
                                            readOnly
                                        />
                                    ) : (
                                        <select
                                            className="form-select bg-light border-0 py-2"
                                            value={formData.gender}
                                            onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                                        >
                                            <option value="Lelaki">{isPoomsaeTeam ? 'Team Putra' : 'Lelaki'}</option>
                                            <option value="Perempuan">{isPoomsaeTeam ? 'Team Putri' : 'Perempuan'}</option>
                                        </select>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label small fw-bold text-muted">Tahun Lahir</label>
                                <input
                                    type="number"
                                    className="form-control bg-light border-0 py-2"
                                    required
                                    value={formData.birth_year}
                                    onChange={(e) => setFormData({ ...formData, birth_year: e.target.value })}
                                    placeholder={`Contoh: ${(getConfig().event_year || new Date().getFullYear()) - 15}`}
                                />
                            </div>

                            <div className="col-md-12">
                                <label className="form-label small fw-bold text-muted">Divisi Umur (Hasil Kalkulasi)</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-white border-0"><BsTrophyFill className="text-warning" /></span>
                                    <input
                                        type="text"
                                        className={`form-control bg-white border-0 fw-bold fs-5 ${(formData.division || '').includes('Tidak') ? 'text-danger' : 'text-dark'}`}
                                        value={formData.division || "Isi tahun lahir..."}
                                        readOnly
                                    />
                                </div>
                            </div>

                            {/* SECTION: SPESIFIKASI DINAMIS */}
                            <div className="col-12 mt-4 mb-2">
                                <h6 className="fw-bold text-dark border-bottom pb-2">Spesifikasi {formData.competition_type} {formData.type}</h6>
                            </div>

                            {/* Poomsae Festival */}
                            {(isPoomsae && isFestival) && (
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold text-muted">Tingkatan Sabuk</label>
                                    <select
                                        className="form-select bg-light border-0 py-2"
                                        required
                                        value={formData.belt}
                                        onChange={(e) => setFormData({ ...formData, belt: e.target.value })}
                                    >
                                        <option value="">Pilih Sabuk...</option>
                                        {getPoomsaeBeltOptions().map(belt => (
                                            <option key={belt} value={belt}>{belt}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Poomsae Kategori Prestasi */}
                            {(isPoomsae && isPrestasi) && (
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold text-muted">Kategori Poomsae</label>
                                    <select
                                        name="poomsae_category"
                                        className="form-select bg-light border-0 py-2"
                                        required
                                        value={normalizePoomsaeCategoryValue(formData.poomsae_category)}
                                        onChange={(e) => handlePoomsaeCategoryChange(e.target.value)}
                                    >
                                        {POOMSAE_CATEGORY_OPTIONS.map(category => (
                                            <option key={category} value={category}>
                                                {category === 'Individual'
                                                    ? 'Individual (Perorangan)'
                                                    : category === 'Pair'
                                                        ? 'Pair (Berpasangan)'
                                                        : category === 'Team'
                                                            ? 'Team (Beregu)'
                                                            : 'Freestyle'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Kyorugi Festival: Berat, Tinggi, Sabuk */}
                            {(isKyorugi && isFestival) && (
                                <>
                                    <div className="col-md-4">
                                        <label className="form-label small fw-bold text-muted">Berat Badan (Kg)</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-0"><BsSpeedometer2 /></span>
                                            <input
                                                type="number"
                                                step="0.1"
                                                className="form-control bg-light border-0 py-2"
                                                required
                                                value={formData.declared_weight}
                                                onChange={(e) => setFormData({ ...formData, declared_weight: e.target.value })}
                                                placeholder="0.0"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label small fw-bold text-muted">Tinggi Badan (cm)</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-0"><BsRulers /></span>
                                            <input
                                                type="number"
                                                className="form-control bg-light border-0 py-2"
                                                required
                                                value={formData.height}
                                                onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label small fw-bold text-muted">Sabuk</label>
                                        <select
                                            className="form-select bg-light border-0 py-2"
                                            required
                                            value={formData.belt}
                                            onChange={(e) => setFormData({ ...formData, belt: e.target.value })}
                                        >
                                            <option value="">Pilih Sabuk...</option>
                                            {KYORUGI_FESTIVAL_BELTS.map(belt => (
                                                <option key={belt} value={belt}>{belt}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}

                            {/* Kyorugi Prestasi: Weight Class */}
                            {(isKyorugi && isPrestasi) && (
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold text-muted">Kelas Tanding (Weight Class)</label>
                                    <select
                                        className="form-select bg-light border-0 py-2"
                                        required
                                        value={formData.weight_class}
                                        onChange={(e) => setFormData({ ...formData, weight_class: e.target.value })}
                                    >
                                        <option value="">-- Pilih Kelas Tanding --</option>
                                        {getFilteredWeightOptions().map(wc => <option key={wc} value={wc}>{wc}</option>)}
                                    </select>
                                </div>
                            )}

                        </div>
                    </div>
                </div>

                {/* Kolom Kanan: Foto & Submit */}
                <div className="col-lg-4">
                    <div className="card border-0 shadow-sm rounded-4 p-4 sticky-top" style={{ top: '2rem' }}>
                        <label className="form-label small fw-bold text-muted mb-3">Foto Formal Atlet</label>
                        <div className="text-center mb-4">
                            <label className="cursor-pointer position-relative d-inline-block">
                                <div className="rounded-4 overflow-hidden border-dashed d-flex align-items-center justify-content-center bg-light" style={{ width: '100%', aspectRatio: '3/4', maxWidth: '220px', borderColor: '#dee2e6' }}>
                                    {previewUrl ? (
                                        <img src={previewUrl} alt="Preview" className="w-100 h-100 object-fit-cover" />
                                    ) : (
                                        <div className="text-center p-3">
                                            <BsCameraFill size={40} className="text-muted mb-2 opacity-50" />
                                            <br />
                                            <span className="text-muted small">Klik untuk upload foto (Max 2MB)</span>
                                        </div>
                                    )}
                                </div>
                                <input type="file" className="d-none" accept="image/*" onChange={handleFileChange} />
                            </label>
                        </div>

                        <button type="submit" className="btn btn-primary w-100 py-3 rounded-pill fw-bold shadow-sm mb-3 d-flex justify-content-center align-items-center gap-2" disabled={isLoading || (formData.division || '').includes("Tidak Layak")}>
                            {isLoading ? (
                                <><div className="spinner-border spinner-border-sm text-white"></div> Menyimpan...</>
                            ) : (
                                <><BsSaveFill /> {isEditMode ? 'Simpan Perubahan' : 'Daftarkan Atlet'}</>
                            )}
                        </button>

                        <div className="alert alert-info bg-info bg-opacity-10 border-0 shadow-sm rounded-4 small d-flex gap-2">
                            <BsInfoCircleFill className="mt-1 flex-shrink-0 text-info" />
                            <span className="text-dark">
                                Sistem memvalidasi kelayakan umur, kelas, sabuk Kyorugi Festival, serta jumlah nama Pair/Team secara otomatis.
                            </span>
                        </div>

                        {(isPoomsaePair || isPoomsaeTeam) && (
                            <div className="alert alert-warning bg-warning bg-opacity-10 border-0 shadow-sm rounded-4 small d-flex gap-2 mt-3">
                                <BsInfoCircleFill className="mt-1 flex-shrink-0 text-warning" />
                                <span className="text-dark">
                                    Pair/Team tetap dihitung sebagai satu entry agar biaya pendaftaran tidak dobel.
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </form>

            <style>{`
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default ManagerAddAthlete;
